<script lang="ts" context="module">
    import { writable } from 'svelte/store';

    export const theme = writable({
        viewportWidth: 1200,
        gutter: '80px',
        fontFamilySans: 'Inter',
        breakpoints: {
            xs: '576px',
            sm: '768px',
            md: '992px',
            lg: '1200px',
            xl: '1400px',
        },
        xsUp: false,
        smUp: false,
        mdUp: false,
        lgUp: false,
        xlUp: false,
        zIndex: {
            bottom: 0,
            base: 10,
            nav: 100,
            cookies: 1000,
        },
        height: {
            topBar: {
                mobile: '56px',
                desktop: '88px',
                negativeMobile: '-56px',
                negativeDesktop: '-88px',
            },
        },
        colors: {
            logo: '#080D4D',
            primary: {
                50: '#E6EAF2',
                100: '#CCD5E4',
                200: '#B3C0D7',
                300: '#9AABC9',
                400: '#8196BC',
                500: '#6782AE',
                600: '#4E6DA1',
                700: '#355893',
                750: '#355893',
                800: '#1B4386',
                900: '#022E79',
                950: '#0f1d34',
            },
            secondary: {
                900: '#1D74E9',
                50: '#E8F1FD',
            },
            common: {
                light: {
                    soft: '#6d85a9',
                    medium: '#515d72',
                    strong: '#1a2c49',
                },
                text: {
                    dark: '#2E2E2E',
                    heading: '#022E79',
                },
                dark: {
                    text: '#BCC4D3',
                    soft: '#707c8d',
                    medium: '#b9c2d0',
                },
                background: {
                    dark: {
                        strong: '#022E79',
                    },
                    light: {
                        gray: '#f6faff',
                        green: '#F6FAFF',
                    },
                },
            },
            transparent: {
                black: '#00000080',
            },
            neutral: {
                50: '#f0f2f5',
                100: '#E4E6E9',
                200: '#D8DADD',
                300: '#CBCED1',
                400: '#bfc1c5',
                600: '#8C8E92',
                700: '#66686B',
            },
            semantic: {
                success: {
                    100: '#F0FBD4',
                    400: '#A4D959',
                    500: '#7BC12A',
                    600: '#60A51E',
                },
                error: {
                    100: '#FFE5D9',
                    400: '#FF7B71',
                    500: '#FF4245',
                    600: '#DB3041',
                },
                warning: {
                    100: '#FEF5D3',
                    400: '#FBC95C',
                    500: '#F9B027',
                    600: '#D68F1C',
                },
            },
            line: {
                soft: '#e4e7e5',
                medium: '#d7dbd8',
                strong: '#bcc2bd',
            },
            white: '#FFFFFF',
            black: '#000000',
            textDark: '#020303',
            junction: {
                go: {
                    50: '#F8F5FE',
                    500: '#7C5FBA',
                },
                plus: {
                    50: '#F5FBFE',
                    500: '#3CB4E8',
                },
                pay: {
                    50: '#FFF9F5',
                    500: '#FF7D37',
                },
                one: {
                    50: '#FFFCF2',
                    500: '#FFC60C',
                },
            },
            title: {
                dark: '#FDE7EA',
            },
        },
        spacing: {
            0: '0px',
            1: '2px',
            2: '4px',
            3: '6px',
            4: '8px',
            5: '10px',
            6: '12px',
            7: '14px',
            8: '16px',
            9: '18px',
            10: '20px',
            11: '22px',
            12: '24px',
            13: '26px',
            14: '28px',
            15: '30px',
            16: '32px',
            17: '34px',
            18: '36px',
            19: '38px',
            20: '40px',
            21: '42px',
            22: '44px',
            23: '46px',
            24: '48px',
            25: '50px',
            26: '52px',
            28: '56px',
            30: '60px',
            31: '62px',
            32: '64px',
            34: '68px',
            36: '72px',
            37: '74px',
            40: '80px',
            80: '160px',
        },
    });
</script>

<script lang="ts">
    // for vite updates without hitting refresh as opposed to static/css/[theme].css
    import './theme.css';

    let innerWidth: number;

    const calculateScreenSize = (viewportWidth: number): void => {
        $theme.viewportWidth = viewportWidth;
        if (viewportWidth >= 0 && viewportWidth <= 575) {
            $theme.gutter = '16px';
            $theme.xsUp = false;
            $theme.smUp = false;
            $theme.mdUp = false;
            $theme.lgUp = false;
            $theme.xlUp = false;
            return;
        }
        if (viewportWidth >= 576 && viewportWidth <= 767) {
            $theme.gutter = '24px';
            $theme.xsUp = true;
            $theme.smUp = false;
            $theme.mdUp = false;
            $theme.lgUp = false;
            $theme.xlUp = false;
            return;
        }
        if (viewportWidth >= 768 && viewportWidth <= 991) {
            $theme.gutter = '40px';
            $theme.xsUp = true;
            $theme.smUp = true;
            $theme.mdUp = false;
            $theme.lgUp = false;
            $theme.xlUp = false;
            return;
        }
        if (viewportWidth >= 992 && viewportWidth <= 1199) {
            $theme.gutter = '80px';
            $theme.xsUp = true;
            $theme.smUp = true;
            $theme.mdUp = true;
            $theme.lgUp = false;
            $theme.xlUp = false;
            return;
        }

        if (viewportWidth >= 1200 && viewportWidth <= 1399) {
            $theme.gutter = '80px';
            $theme.xsUp = true;
            $theme.smUp = true;
            $theme.mdUp = true;
            $theme.lgUp = true;
            $theme.xlUp = false;
            return;
        }

        $theme.gutter = '80px';
        $theme.xsUp = true;
        $theme.smUp = true;
        $theme.mdUp = true;
        $theme.lgUp = true;
        $theme.xlUp = true;
    };

    $: if (innerWidth) {
        calculateScreenSize(innerWidth);
    }
</script>

<svelte:window bind:innerWidth />

<div
    style:--colors-white={$theme.colors.white}
    style:--colors-black={$theme.colors.black}
    style:--colors-primary-50={$theme.colors.primary[50]}
    style:--colors-primary-100={$theme.colors.primary[100]}
    style:--colors-primary-200={$theme.colors.primary[200]}
    style:--colors-primary-300={$theme.colors.primary[300]}
    style:--colors-primary-400={$theme.colors.primary[400]}
    style:--colors-primary-500={$theme.colors.primary[500]}
    style:--colors-primary-600={$theme.colors.primary[600]}
    style:--colors-primary-700={$theme.colors.primary[700]}
    style:--colors-primary-750={$theme.colors.primary[750]}
    style:--colors-primary-800={$theme.colors.primary[800]}
    style:--colors-primary-900={$theme.colors.primary[900]}
    style:--colors-secondary-900={$theme.colors.secondary[900]}
    style:--colors-secondary-50={$theme.colors.secondary[50]}
    style:--colors-common-text-heading={$theme.colors.common.text.heading}
    style:--colors-common-text-dark={$theme.colors.common.text.dark}
    style:--colors-common-light-soft={$theme.colors.common.light.soft}
    style:--colors-common-light-medium={$theme.colors.common.light.medium}
    style:--colors-common-dark-soft={$theme.colors.common.dark.soft}
    style:--colors-common-dark-medium={$theme.colors.common.dark.medium}
    style:--colors-common-background-dark-strong={$theme.colors.common.background.dark.strong}
    style:--colors-common-background-light-gray={$theme.colors.common.background.light.gray}
    style:--colors-common-background-light-green={$theme.colors.common.background.light.green}
    style:--colors-line-soft={$theme.colors.line.soft}
    style:--colors-line-medium={$theme.colors.line.medium}
    style:--colors-line-strong={$theme.colors.line.strong}
    style:--colors-neutral-50={$theme.colors.neutral[50]}
    style:--colors-neutral-200={$theme.colors.neutral[200]}
    style:--colors-neutral-100={$theme.colors.neutral[100]}
    style:--colors-neutral-300={$theme.colors.neutral[300]}
    style:--colors-neutral-400={$theme.colors.neutral[400]}
    style:--colors-neutral-600={$theme.colors.neutral[600]}
    style:--colors-neutral-700={$theme.colors.neutral[700]}
    style:--colors-semantic-success-100={$theme.colors.semantic.success[100]}
    style:--colors-semantic-success-400={$theme.colors.semantic.success[400]}
    style:--colors-semantic-success-500={$theme.colors.semantic.success[500]}
    style:--colors-semantic-success-600={$theme.colors.semantic.success[600]}
    style:--colors-semantic-error-100={$theme.colors.semantic.error[100]}
    style:--colors-semantic-error-400={$theme.colors.semantic.error[400]}
    style:--colors-semantic-error-500={$theme.colors.semantic.error[500]}
    style:--colors-semantic-error-600={$theme.colors.semantic.error[600]}
    style:--colors-semantic-warning-100={$theme.colors.semantic.warning[100]}
    style:--colors-semantic-warning-400={$theme.colors.semantic.warning[400]}
    style:--colors-semantic-warning-500={$theme.colors.semantic.warning[500]}
    style:--colors-semantic-warning-600={$theme.colors.semantic.warning[600]}
    style:--colors-text-dark={$theme.colors.textDark}
    style:--colors-junction-plus-50={$theme.colors.junction.plus[50]}
    style:--colors-junction-plus-500={$theme.colors.junction.plus[500]}
    style:--colors-junction-go-50={$theme.colors.junction.go[50]}
    style:--colors-junction-go-500={$theme.colors.junction.go[500]}
    style:--colors-junction-pay-50={$theme.colors.junction.pay[50]}
    style:--colors-junction-pay-500={$theme.colors.junction.pay[500]}
    style:--colors-junction-one-50={$theme.colors.junction.one[50]}
    style:--colors-junction-one-500={$theme.colors.junction.one[500]}
    style:--colors-title-dark={$theme.colors.title.dark}
    style:--colors-transparent-black={$theme.colors.transparent.black}
    style:--spacing-0={$theme.spacing[0]}
    style:--spacing-01={$theme.spacing[1]}
    style:--spacing-02={$theme.spacing[2]}
    style:--spacing-03={$theme.spacing[3]}
    style:--spacing-04={$theme.spacing[4]}
    style:--spacing-05={$theme.spacing[5]}
    style:--spacing-06={$theme.spacing[6]}
    style:--spacing-07={$theme.spacing[7]}
    style:--spacing-08={$theme.spacing[8]}
    style:--spacing-09={$theme.spacing[9]}
    style:--spacing-10={$theme.spacing[10]}
    style:--spacing-11={$theme.spacing[11]}
    style:--spacing-12={$theme.spacing[12]}
    style:--spacing-13={$theme.spacing[13]}
    style:--spacing-14={$theme.spacing[14]}
    style:--spacing-15={$theme.spacing[15]}
    style:--spacing-16={$theme.spacing[16]}
    style:--spacing-17={$theme.spacing[17]}
    style:--spacing-18={$theme.spacing[18]}
    style:--spacing-19={$theme.spacing[19]}
    style:--spacing-20={$theme.spacing[20]}
    style:--spacing-21={$theme.spacing[21]}
    style:--spacing-22={$theme.spacing[22]}
    style:--spacing-23={$theme.spacing[23]}
    style:--spacing-24={$theme.spacing[24]}
    style:--spacing-25={$theme.spacing[25]}
    style:--spacing-26={$theme.spacing[26]}
    style:--spacing-28={$theme.spacing[28]}
    style:--spacing-30={$theme.spacing[30]}
    style:--spacing-31={$theme.spacing[31]}
    style:--spacing-32={$theme.spacing[32]}
    style:--spacing-34={$theme.spacing[34]}
    style:--spacing-36={$theme.spacing[36]}
    style:--spacing-37={$theme.spacing[37]}
    style:--spacing-40={$theme.spacing[40]}
    style:--spacing-80={$theme.spacing[80]}
    style:--breakpoints-xs={$theme.breakpoints.xs}
    style:--breakpoints-sm={$theme.breakpoints.sm}
    style:--breakpoints-md={$theme.breakpoints.md}
    style:--breakpoints-lg={$theme.breakpoints.lg}
    style:--breakpoints-xl={$theme.breakpoints.xl}
    style:--zIndex-base={$theme.zIndex.base}
    style:--zIndex-bottom={$theme.zIndex.bottom}
    style:--zIndex-nav={$theme.zIndex.nav}
    style:--zIndex-cookies={$theme.zIndex.cookies}
    style:--height-top-bar-mobile={$theme.height.topBar.mobile}
    style:--height-top-bar-desktop={$theme.height.topBar.desktop}
    style:--height-top-bar-negative-mobile={$theme.height.topBar.negativeMobile}
    style:--height-top-bar-negative-desktop={$theme.height.topBar.negativeDesktop}
    style:--font-family-sans={$theme.fontFamilySans}
>
    <slot />
</div>
